import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { addDays, format, getDay, parseISO, startOfToday } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { supabaseUntyped } from "@/integrations/supabase/untyped";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import {
  buildUnifiedEvents,
  dayKey,
  groupUnifiedEventsByDay,
  type ExternalBookingRow,
  type UnifiedVendorEvent,
} from "@/components/company/externalBookings/types";
import { computeDayLoadFlags } from "@/lib/scheduler";
import type { Booking } from "@/types";

const SLOT_LABELS: { slot: '08:00'|'12:00'|'16:00'; label: string }[] = [
  { slot: '08:00', label: '08:00 - 12:00' },
  { slot: '12:00', label: '12:00 - 16:00' },
  { slot: '16:00', label: '16:00 - 20:00' },
];

interface SlotAvailability {
  slot: '08:00'|'12:00'|'16:00';
  capacity: number;
  blocked: boolean;
}

interface DayAvailability {
  date: string;
  slots: SlotAvailability[];
}

interface AvailabilityEditorProps {
  onSave?: (days: DayAvailability[]) => void;
}

type EditorMode = "simple" | "advanced";

type WeeklyTemplateDay = {
  weekday: number;
  label: string;
  slots: SlotAvailability[];
};

type AssistedPlannerState = {
  teamsBySlot: Record<SlotAvailability["slot"], number>;
  jobsPerTeam: number;
  safetyMarginPct: number;
};

const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
function weekdayLabel(t: (k: string) => string, weekday: number): string {
  return t(`availability.weekday.${weekday}`);
}

const SIMPLE_CAPACITY_PRESETS = [0, 1, 2, 5, 10];

/** Shape returned by `select('id, companies(id)')` on `profiles`. */
type ProfileWithCompanies = { id: string; companies: Array<{ id: string }> | null };
type RawSlotRow = { date: string; slot: string; capacity: number | null; blocked: boolean | null };
type RawDayRow = { date: string; capacity: number | null; blocked: boolean | null };


function getCapacityHelper(capacity: number, blocked: boolean, t: (k: string) => string, tx: (k: string, v: Record<string, string | number>) => string): string {
  if (blocked) return t('availability.helper.blocked');
  if (capacity <= 0) return t('availability.helper.zero');
  if (capacity === 1) return t('availability.helper.one');
  return tx('availability.helper.n', { capacity });
}

function cloneSlots(slots: SlotAvailability[]): SlotAvailability[] {
  return slots.map((slot) => ({ ...slot }));
}

function sameSlotConfig(a: SlotAvailability[], b: SlotAvailability[]): boolean {
  return a.every((slot, idx) => {
    const other = b[idx];
    return !!other && slot.slot === other.slot && slot.capacity === other.capacity && slot.blocked === other.blocked;
  });
}

function getWeekdayTemplate(days: DayAvailability[], t: (k: string) => string): WeeklyTemplateDay[] {
  return WEEKDAY_ORDER.map((weekday) => {
    const matchingDay = days.find((day) => getDay(parseISO(day.date)) === weekday);
    return {
      weekday,
      label: weekdayLabel(t, weekday),
      slots: matchingDay ? cloneSlots(matchingDay.slots) : SLOT_LABELS.map((s) => ({ slot: s.slot, capacity: 0, blocked: true })),
    };
  });
}

function getOverrideDates(days: DayAvailability[], weeklyTemplate: WeeklyTemplateDay[]): string[] {
  return days
    .filter((day) => {
      const weekday = getDay(parseISO(day.date));
      const template = weeklyTemplate.find((row) => row.weekday === weekday);
      return template ? !sameSlotConfig(day.slots, template.slots) : false;
    })
    .map((day) => day.date);
}

function summarizeSlots(
  slots: SlotAvailability[],
  t: (k: string) => string,
  tx: (k: string, v: Record<string, string | number>) => string
): string {
  return slots
    .map((slot) => {
      const label = SLOT_LABELS.find((item) => item.slot === slot.slot)?.label ?? slot.slot;
      const compactLabel = label.split(" - ")[0];
      const status = slot.blocked
        ? t("availability.slot_closed_short")
        : slot.capacity === 1
          ? t("availability.job_count")
          : tx("availability.jobs_count", { n: slot.capacity });
      return tx("availability.slot_summary", { label: compactLabel, status });
    })
    .join(" • ");
}

function getSuggestedCapacity(teams: number, jobsPerTeam: number, safetyMarginPct: number): number {
  if (teams <= 0 || jobsPerTeam <= 0) return 0;
  const normalizedMargin = Math.min(80, Math.max(0, safetyMarginPct));
  const raw = teams * jobsPerTeam * (1 - normalizedMargin / 100);
  return Math.max(1, Math.floor(raw));
}

function getCapacitySliderMax(capacity: number): number {
  if (capacity <= 10) return 10;
  if (capacity <= 15) return 15;
  if (capacity <= 20) return 20;
  if (capacity <= 30) return 30;
  return Math.ceil(capacity / 10) * 10;
}

export default function AvailabilityEditor({ onSave }: AvailabilityEditorProps) {
  const { t, tx } = useI18n();
  const capacityExamples = useMemo(
    () => [
      {
        title: t("availability.example_title"),
        lines: [t("availability.example_line_1"), t("availability.example_line_2")],
      },
    ],
    [t]
  );
  const initial = useMemo(() => {
    const today = startOfToday();
    return Array.from({ length: 14 }).map((_, i) => ({
      date: format(addDays(today, i), 'yyyy-MM-dd'),
      slots: SLOT_LABELS.map(s => ({ slot: s.slot, capacity: 5, blocked: false })),
    }));
  }, []);

  const [days, setDays] = useState<DayAvailability[]>(initial);
  const [editorMode, setEditorMode] = useState<EditorMode>("simple");
  const [weeklyTemplate, setWeeklyTemplate] = useState<WeeklyTemplateDay[]>(() => getWeekdayTemplate(initial, t));
  const [overriddenDates, setOverriddenDates] = useState<string[]>([]);
  const [expandedDays, setExpandedDays] = useState<string[]>(initial[0]?.date ? [initial[0].date] : []);
  const [assistedPlanner, setAssistedPlanner] = useState<AssistedPlannerState>({
    teamsBySlot: {
      "08:00": 2,
      "12:00": 1,
      "16:00": 1,
    },
    jobsPerTeam: 1,
    safetyMarginPct: 20,
  });
  const [resolvedCompanyId, setResolvedCompanyId] = useState<string | null>(null);
  const [unifiedEvents, setUnifiedEvents] = useState<UnifiedVendorEvent[]>([]);
  const { toast } = useToast();

  // Load existing vendor availability and hydrate the editor state so changes persist across refreshes
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, companies(id)')
          .eq('user_id', session.user.id)
          .single();
        const companyId = (profile as ProfileWithCompanies | null)?.companies?.[0]?.id;
        if (!companyId) return;
        setResolvedCompanyId(companyId);
        const start = days[0]?.date;
        const end = days[days.length - 1]?.date;
        // Prefer slot availability; fallback to legacy per-day availability if slots not set yet.
        const { data: slotsRows } = await supabaseUntyped
          .from('vendor_availability_slots')
          .select('date, slot, capacity, blocked')
          .eq('company_id', companyId)
          .gte('date', start)
          .lte('date', end);

        if (Array.isArray(slotsRows) && slotsRows.length) {
          const typedSlotRows = slotsRows as RawSlotRow[];
          const nextDays = initial.map(d => {
            const rows = typedSlotRows.filter((r) => r.date === d.date);
            if (!rows.length) return d;
            const nextSlots = d.slots.map(s => {
              const r = rows.find((x) => x.slot === s.slot);
              return r ? { slot: s.slot, capacity: Number(r.capacity ?? 5), blocked: !!r.blocked } : s;
            });
            return { ...d, slots: nextSlots };
          });
          const nextTemplate = getWeekdayTemplate(nextDays, t);
          setDays(nextDays);
          setWeeklyTemplate(nextTemplate);
          setOverriddenDates(getOverrideDates(nextDays, nextTemplate));
          return;
        }

        // Fallback: hydrate from legacy per-day vendor_availability
        const { data: dayRows } = await supabaseUntyped
          .from('vendor_availability')
          .select('date, capacity, blocked')
          .eq('company_id', companyId)
          .gte('date', start)
          .lte('date', end);
        if (Array.isArray(dayRows) && dayRows.length) {
          const typedDayRows = dayRows as RawDayRow[];
          const nextDays = initial.map(d => {
            const row = typedDayRows.find((r) => r.date === d.date);
            if (!row) return d;
            const cap = typeof row.capacity === 'number' ? Number(row.capacity) : 5;
            const blocked = !!row.blocked;
            return { ...d, slots: d.slots.map(s => ({ ...s, capacity: cap, blocked })) };
          });
          const nextTemplate = getWeekdayTemplate(nextDays, t);
          setDays(nextDays);
          setWeeklyTemplate(nextTemplate);
          setOverriddenDates(getOverrideDates(nextDays, nextTemplate));
        }
      } catch {
        // Hydration is best-effort: if profile/availability can't be loaded
        // we keep the `initial` template so the editor stays usable.
      }
    })();
    // `days` is intentionally excluded: this hydrate-on-mount effect must
    // run only when the `initial` template changes, not on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  // Sprint 3: load the unified vendor load (platform + external) for the 14
  // days under edit, so we can compare it against published capacity.
  useEffect(() => {
    let active = true;
    if (!resolvedCompanyId) return;
    (async () => {
      try {
        const start = days[0]?.date;
        const end = days[days.length - 1]?.date;
        if (!start || !end) return;

        const { data: bookingsRows } = await supabase
          .from('bookings')
          .select('id, status, scheduled_date, scheduled_time, window_start_at, window_end_at, checkout_at, checkin_at, service_id')
          .eq('company_id', resolvedCompanyId)
          .gte('scheduled_date', start)
          .lte('scheduled_date', end);

        const { data: externalRows } = await supabaseUntyped
          .from('external_bookings')
          .select('*')
          .eq('company_id', resolvedCompanyId);

        const events = buildUnifiedEvents({
          companyId: resolvedCompanyId,
          bookings: (bookingsRows || []) as Booking[],
          externalRows: (externalRows || []) as unknown as ExternalBookingRow[],
        });
        if (active) setUnifiedEvents(events);
      } catch {
        if (active) setUnifiedEvents([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [resolvedCompanyId, days]);

  const eventsByDay = useMemo(() => groupUnifiedEventsByDay(unifiedEvents), [unifiedEvents]);

  const dayCapacityTotals = useMemo(() => {
    const map = new Map<string, { capacity: number; blocked: boolean }>();
    for (const d of days) {
      const totalCapacity = d.slots.reduce(
        (acc, s) => acc + (s.blocked ? 0 : Math.max(0, s.capacity)),
        0
      );
      const blocked = d.slots.every((s) => s.blocked);
      map.set(d.date, { capacity: totalCapacity, blocked });
    }
    return map;
  }, [days]);

  const dayLoadInfo = useMemo(() => {
    const map = new Map<
      string,
      {
        load: number;
        capacity: number;
        blocked: boolean;
        flags: ReturnType<typeof computeDayLoadFlags>;
      }
    >();
    for (const d of days) {
      const list = eventsByDay.get(dayKey(parseISO(d.date))) || [];
      const cap = dayCapacityTotals.get(d.date) || { capacity: 0, blocked: false };
      const flags = computeDayLoadFlags({
        load: list.length,
        capacity: cap.capacity,
        blocked: cap.blocked,
        sameDayTurnoverRows: 0,
      });
      map.set(d.date, { load: list.length, capacity: cap.capacity, blocked: cap.blocked, flags });
    }
    return map;
  }, [days, eventsByDay, dayCapacityTotals]);

  const persist = async () => {
    // Upsert 14 days for the vendor's company inferred from profile
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({ title: t('availability.not_signed_in'), description: t('availability.sign_in_to_save'), variant: 'destructive' });
      return;
    }
    const { data: profile } = await supabase
      .from('profiles')
      .select('id, user_id, companies(id)')
      .eq('user_id', session.user.id)
      .single();
    const companyId = (profile as ProfileWithCompanies | null)?.companies?.[0]?.id;
    if (!companyId) {
      toast({ title: t('availability.no_company'), description: t('availability.complete_onboarding'), variant: 'destructive' });
      return;
    }
    const slotPayload = days.flatMap(d => d.slots.map(s => ({
      company_id: companyId,
      date: d.date,
      slot: s.slot,
      capacity: s.capacity,
      blocked: s.blocked,
    })));
    const { error: slotErr } = await supabaseUntyped
      .from('vendor_availability_slots')
      .upsert(slotPayload, { onConflict: 'company_id,date,slot' });
    if (slotErr) {
      toast({ title: t('availability.save_failed'), description: slotErr.message, variant: 'destructive' });
      return;
    }

    // Also maintain legacy per-day aggregate so older reads keep working.
    const dayAggPayload = days.map(d => {
      const anyOpen = d.slots.some(s => !s.blocked && s.capacity > 0);
      const capSum = d.slots.reduce((sum, s) => sum + (s.blocked ? 0 : Math.max(0, s.capacity)), 0);
      return {
        company_id: companyId,
        date: d.date,
        capacity: anyOpen ? capSum : 0,
        blocked: !anyOpen,
      };
    });
    await supabaseUntyped.from('vendor_availability').upsert(dayAggPayload, { onConflict: 'company_id,date' });

    toast({ title: t('availability.saved'), description: t('availability.changes_live') });
    onSave?.(days);
  };

  const updateSlot = (dayIdx: number, slot: SlotAvailability['slot'], patch: Partial<SlotAvailability>) => {
    setDays(prev => prev.map((d, i) => {
      if (i !== dayIdx) return d;
      return {
        ...d,
        slots: d.slots.map(s => s.slot === slot ? { ...s, ...patch } : s)
      };
    }));
  };

  const setOverrideForDay = (date: string, nextSlots: SlotAvailability[]) => {
    const weekday = getDay(parseISO(date));
    const template = weeklyTemplate.find((row) => row.weekday === weekday);
    setOverriddenDates((prev) => {
      const without = prev.filter((item) => item !== date);
      if (!template || sameSlotConfig(nextSlots, template.slots)) return without;
      return [...without, date];
    });
  };

  const updateDaySlotByDate = (date: string, slot: SlotAvailability["slot"], next: SlotAvailability) => {
    const nextDays = days.map((day) => {
      if (day.date !== date) return day;
      const nextSlots = day.slots.map((item) => (item.slot === slot ? next : item));
      return { ...day, slots: nextSlots };
    });
    const updatedDay = nextDays.find((day) => day.date === date);
    setDays(nextDays);
    if (updatedDay) setOverrideForDay(date, updatedDay.slots);
  };

  const resetDayToTemplate = (date: string) => {
    const weekday = getDay(parseISO(date));
    const template = weeklyTemplate.find((row) => row.weekday === weekday);
    if (!template) return;
    setDays((prev) => prev.map((day) => (day.date === date ? { ...day, slots: cloneSlots(template.slots) } : day)));
    setOverriddenDates((prev) => prev.filter((item) => item !== date));
  };

  const assistedSuggestedSlots = useMemo(
    () =>
      SLOT_LABELS.map(({ slot }) => {
        const teams = Math.max(0, assistedPlanner.teamsBySlot[slot] || 0);
        const capacity = getSuggestedCapacity(teams, assistedPlanner.jobsPerTeam, assistedPlanner.safetyMarginPct);
        return {
          slot,
          capacity,
          blocked: teams <= 0,
        };
      }),
    [assistedPlanner]
  );

  const applyAssistedPlannerToWeek = () => {
    const suggestedSlots = cloneSlots(assistedSuggestedSlots);
    const nextTemplate = WEEKDAY_ORDER.map((weekday) => ({
      weekday,
      label: weekdayLabel(t, weekday),
      slots: cloneSlots(suggestedSlots),
    }));

    setWeeklyTemplate(nextTemplate);
    setDays((prev) =>
      prev.map((day) => {
        if (overriddenDates.includes(day.date)) return day;
        return {
          ...day,
          slots: cloneSlots(suggestedSlots),
        };
      })
    );
  };

  const applyDayAsTemplate = (date: string) => {
    const sourceDay = days.find((day) => day.date === date);
    if (!sourceDay) return;

    const weekday = getDay(parseISO(date));
    const nextTemplate = weeklyTemplate.map((row) =>
      row.weekday === weekday ? { ...row, slots: cloneSlots(sourceDay.slots) } : row
    );
    const nextDays = days.map((day) => {
      if (getDay(parseISO(day.date)) !== weekday) return day;
      if (day.date === date || !overriddenDates.includes(day.date)) {
        return { ...day, slots: cloneSlots(sourceDay.slots) };
      }
      return day;
    });

    setWeeklyTemplate(nextTemplate);
    setDays(nextDays);
    setOverriddenDates(getOverrideDates(nextDays, nextTemplate));
  };

  const toggleExpandedDay = (date: string) => {
    setExpandedDays((prev) => (prev.includes(date) ? prev.filter((item) => item !== date) : [...prev, date]));
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="break-words">{t('availability.title')}</CardTitle>
        <CardDescription className="break-words">
{t('availability.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="max-w-full overflow-x-auto">
        <CapacityVsLoadOverview dayLoadInfo={dayLoadInfo} />

        <div className="mb-4 rounded-xl border border-primary/15 bg-primary/5 p-4 space-y-3">
          <div className="text-sm font-semibold text-gray-900">{t("availability.how_capacity_works")}</div>
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              {t("availability.capacity_intro_1")}
            </p>
            <p>
              {t("availability.capacity_intro_2")}
            </p>
          </div>
          {capacityExamples.map((example) => (
            <div key={example.title} className="rounded-lg border border-primary/10 bg-white/80 p-3">
              <div className="text-sm font-medium text-gray-900">{example.title}</div>
              <div className="mt-2 space-y-1 text-sm text-gray-700">
                {example.lines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-600">
            {t("availability.capacity_rule")}
          </p>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          <Button type="button" variant={editorMode === "simple" ? "default" : "outline"} onClick={() => setEditorMode("simple")}>
            {t("availability.mode_simple")}
          </Button>
          <Button type="button" variant={editorMode === "advanced" ? "default" : "outline"} onClick={() => setEditorMode("advanced")}>
            {t("availability.mode_advanced")}
          </Button>
        </div>

        {editorMode === "simple" ? (
          <div className="space-y-6">
            <div className="rounded-xl border border-primary/15 bg-primary/5 p-4 space-y-4">
              <div>
                <div className="text-sm font-semibold text-gray-900">{t("availability.assisted_title")}</div>
                <p className="mt-1 text-sm text-gray-600">
                  {t("availability.assisted_desc")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                {SLOT_LABELS.map(({ slot, label }) => (
                  <div key={slot} className="rounded-lg border border-primary/10 bg-white/80 p-3 space-y-2">
                    <div className="text-xs font-medium text-gray-700">{label}</div>
                    <div className="text-xs text-gray-500">{t("availability.teams_available")}</div>
                    <Input
                      type="number"
                      min={0}
                      className="h-9"
                      value={assistedPlanner.teamsBySlot[slot]}
                      onChange={(e) =>
                        setAssistedPlanner((prev) => ({
                          ...prev,
                          teamsBySlot: {
                            ...prev.teamsBySlot,
                            [slot]: Math.max(0, parseInt(e.target.value, 10) || 0),
                          },
                        }))
                      }
                    />
                  </div>
                ))}

                <div className="rounded-lg border border-primary/10 bg-white/80 p-3 space-y-2">
                  <div className="text-xs font-medium text-gray-700">{t("availability.avg_yield")}</div>
                  <div className="text-xs text-gray-500">{t("availability.jobs_per_team")}</div>
                  <Input
                    type="number"
                    min={1}
                    className="h-9"
                    value={assistedPlanner.jobsPerTeam}
                    onChange={(e) =>
                      setAssistedPlanner((prev) => ({
                        ...prev,
                        jobsPerTeam: Math.max(1, parseInt(e.target.value, 10) || 1),
                      }))
                    }
                  />
                </div>

                <div className="rounded-lg border border-primary/10 bg-white/80 p-3 space-y-2">
                  <div className="text-xs font-medium text-gray-700">{t("availability.safety_margin")}</div>
                  <div className="text-xs text-gray-500">{t("availability.safety_margin_pct")}</div>
                  <Input
                    type="number"
                    min={0}
                    max={80}
                    className="h-9"
                    value={assistedPlanner.safetyMarginPct}
                    onChange={(e) =>
                      setAssistedPlanner((prev) => ({
                        ...prev,
                        safetyMarginPct: Math.min(80, Math.max(0, parseInt(e.target.value, 10) || 0)),
                      }))
                    }
                  />
                </div>
              </div>

              <div className="rounded-lg border border-primary/10 bg-white/80 p-4 space-y-3">
                <div className="text-sm font-medium text-gray-900">{t("availability.suggested_capacity")}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {SLOT_LABELS.map(({ slot, label }) => {
                    const suggested = assistedSuggestedSlots.find((item) => item.slot === slot) || { slot, capacity: 0, blocked: true };
                    return (
                      <div key={slot} className="rounded-lg border border-gray-100 p-3 bg-gray-50">
                        <div className="text-xs font-medium text-gray-700">{label}</div>
                        <div className="mt-2 text-sm text-gray-900">
                          {suggested.blocked ? t('availability.close_slot') : tx('availability.suggested_n', { n: suggested.capacity })}
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          {suggested.blocked
                            ? t("availability.suggested_blocked_hint")
                            : tx("availability.suggested_jobs_hint", { n: suggested.capacity, jobs: suggested.capacity === 1 ? t("availability.job_count") : tx("availability.jobs_count", { n: suggested.capacity }) })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button type="button" onClick={applyAssistedPlannerToWeek}>
                    {t("availability.apply_weekdays")}
                  </Button>
                  <div className="text-xs text-gray-600">
                    {t("availability.custom_days_note")}
                  </div>
                </div>
              </div>

              <AssistedPlannerV2Preview />
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">{t("availability.configure_14_title")}</div>
              <p className="mt-1 text-sm text-gray-600">
                {t("availability.configure_14_desc")}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <div className="text-xs text-gray-600 border border-gray-200 bg-white rounded-full px-3 py-1">
                  {tx("availability.days_standard", { n: days.length - overriddenDates.length })}
                </div>
                <div className="text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded-full px-3 py-1">
                  {tx("availability.days_custom", { n: overriddenDates.length })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {days.map((day) => {
                const isExpanded = expandedDays.includes(day.date);
                const isOverride = overriddenDates.includes(day.date);
                const weekday = getDay(parseISO(day.date));
                const loadInfo = dayLoadInfo.get(day.date);
                return (
                  <div key={day.date} className={`rounded-xl border p-4 space-y-3 ${isOverride ? "border-amber-300 bg-amber-50/30" : "border-gray-200 bg-white"}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{format(parseISO(day.date), "EEE d MMM")}</div>
                        <div className="text-xs text-gray-600 mt-1">{summarizeSlots(day.slots, t, tx)}</div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {isOverride ? (
                          <div className="text-xs text-amber-700 border border-amber-300 bg-white rounded-full px-2 py-1">
                            {t("availability.exception")}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500 border border-gray-200 bg-white rounded-full px-2 py-1">
                            {t("availability.standard_base")}
                          </div>
                        )}
                        <Button type="button" variant="outline" size="sm" onClick={() => toggleExpandedDay(day.date)}>
                          {isExpanded ? t('availability.close_details') : t('availability.open_details')}
                        </Button>
                      </div>
                    </div>

                    <DayLoadWidget info={loadInfo} />


                    {isExpanded ? (
                      <div className="space-y-3 border-t pt-3">
                        {SLOT_LABELS.map(({ slot, label }) => {
                          const selected = day.slots.find((item) => item.slot === slot) || { slot, capacity: 0, blocked: true };
                          const sliderMax = getCapacitySliderMax(selected.capacity);
                          return (
                            <div key={slot} className="rounded-lg border border-gray-100 bg-white p-3 space-y-3">
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-800">{label}</div>
                                  <div className="text-xs text-gray-500">
                                    {selected.blocked ? t("availability.slot_closed_label") : tx("availability.jobs_available", { n: selected.capacity })}
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={selected.blocked ? "default" : "outline"}
                                  onClick={() =>
                                    updateDaySlotByDate(day.date, slot, {
                                      slot,
                                      capacity: selected.blocked ? Math.max(1, selected.capacity) : 0,
                                      blocked: !selected.blocked,
                                    })
                                  }
                                >
                                  {selected.blocked ? t('availability.reopen_slot') : t('availability.close_slot')}
                                </Button>
                              </div>

                              {selected.blocked ? (
                                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                                  {t("availability.slot_no_jobs_hint")}
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-3">
                                    <Slider
                                      min={0}
                                      max={sliderMax}
                                      step={1}
                                      value={[selected.capacity]}
                                      onValueChange={(value) =>
                                        updateDaySlotByDate(day.date, slot, {
                                          slot,
                                          capacity: value[0] ?? 0,
                                          blocked: false,
                                        })
                                      }
                                    />
                                    <Input
                                      type="number"
                                      min={0}
                                      className="h-9 w-24"
                                      value={selected.capacity}
                                      onChange={(e) =>
                                        updateDaySlotByDate(day.date, slot, {
                                          slot,
                                          capacity: Math.max(0, parseInt(e.target.value, 10) || 0),
                                          blocked: false,
                                        })
                                      }
                                    />
                                  </div>

                                  <div className="flex flex-wrap gap-2">
                                    {SIMPLE_CAPACITY_PRESETS.map((value) => (
                                      <Button
                                        key={value}
                                        type="button"
                                        size="sm"
                                        variant={selected.capacity === value ? "default" : "outline"}
                                        className="h-8 px-3"
                                        onClick={() => updateDaySlotByDate(day.date, slot, { slot, capacity: value, blocked: false })}
                                      >
                                        {value}
                                      </Button>
                                    ))}
                                  </div>
                                </>
                              )}

                              <div className="text-xs text-gray-600">{getCapacityHelper(selected.capacity, selected.blocked, t, tx)}</div>
                            </div>
                          );
                        })}

                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="sm" onClick={() => applyDayAsTemplate(day.date)}>
                            {tx("availability.use_weekday_rule", { weekday: weekdayLabel(t, weekday) })}
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => resetDayToTemplate(day.date)}>
                            {tx("availability.reset_weekday", { weekday: weekdayLabel(t, weekday) })}
                          </Button>
                        </div>
                        <div className="text-xs text-gray-600">
                          {tx("availability.weekday_rule_hint", { weekday: weekdayLabel(t, weekday) })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">{t("availability.advanced_mode")}</div>
              <p className="mt-1 text-sm text-gray-600">
                {t("availability.advanced_desc")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 min-w-0">
              {days.map((d, idx) => (
                <div key={d.date} className={`p-3 border rounded-xl space-y-2 ${overriddenDates.includes(d.date) ? "border-amber-300 bg-amber-50/30" : ""}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm font-medium">{format(parseISO(d.date), 'EEE d MMM')}</div>
                    {overriddenDates.includes(d.date) ? (
                      <div className="text-[11px] text-amber-700 border border-amber-300 bg-white rounded-full px-2 py-1">{t("availability.exception")}</div>
                    ) : null}
                  </div>
                  <DayLoadWidget info={dayLoadInfo.get(d.date)} compact />

                  {SLOT_LABELS.map(({ slot, label }) => {
                    const s = d.slots.find(x => x.slot === slot) || { slot, capacity: 5, blocked: false };
                    return (
                      <div key={slot} className="rounded-lg border border-gray-100 p-2 space-y-2">
                        <div className="text-xs font-medium text-gray-700">{label}</div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t('availability.capacity_jobs')}
                          </span>
                          <Input
                            type="number"
                            className="w-20 h-8"
                            value={s.capacity}
                            onChange={(e) => {
                              const capacity = parseInt(e.target.value, 10) || 0;
                              updateSlot(idx, slot, { capacity });
                              const nextSlots = d.slots.map((item) => (item.slot === slot ? { ...item, capacity } : item));
                              setOverrideForDay(d.date, nextSlots);
                            }}
                            disabled={s.blocked}
                          />
                        </div>
                        <div className="text-xs text-gray-600 leading-relaxed">
                          {getCapacityHelper(s.capacity, s.blocked, t, tx)}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">{t('availability.blocked_slot')}</span>
                          <Switch
                            checked={s.blocked}
                            onCheckedChange={(v) => {
                              updateSlot(idx, slot, { blocked: Boolean(v), capacity: v ? 0 : Math.max(1, s.capacity) });
                              const nextSlots = d.slots.map((item) =>
                                item.slot === slot ? { ...item, blocked: Boolean(v), capacity: v ? 0 : Math.max(1, item.capacity) } : item
                              );
                              setOverrideForDay(d.date, nextSlots);
                            }}
                          />
                        </div>
                        <div className="text-xs text-gray-500">
                          {t("availability.close_slot_hint")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button onClick={persist}>{t('availability.save')}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

type DayLoadWidgetInfo = {
  load: number;
  capacity: number;
  blocked: boolean;
  flags: ReturnType<typeof computeDayLoadFlags>;
};

function AssistedPlannerV2Preview() {
  const { t } = useI18n();
  return (
    <div className="rounded-xl border border-dashed border-gray-300 bg-white/70 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold text-gray-900">
          {t("availability.v2_title")}
        </div>
        <Badge variant="outline" className="border-primary/30 text-primary">
          {t("availability.v2_badge")}
        </Badge>
      </div>
      <p className="text-sm text-gray-600">
        {t("availability.v2_desc")}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1">
          <div className="text-xs font-medium text-gray-700">{t("availability.v2_absences")}</div>
          <Input type="text" className="h-9" placeholder={t("availability.v2_absences_ph")} disabled />
          <div className="text-[11px] text-gray-500">{t("availability.v2_absences_hint")}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1">
          <div className="text-xs font-medium text-gray-700">{t("availability.v2_duration")}</div>
          <Input type="number" className="h-9" placeholder={t("availability.v2_duration_ph")} disabled />
          <div className="text-[11px] text-gray-500">{t("availability.v2_duration_hint")}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1">
          <div className="text-xs font-medium text-gray-700">{t("availability.v2_buffer")}</div>
          <Input type="number" className="h-9" placeholder={t("availability.v2_buffer_ph")} disabled />
          <div className="text-[11px] text-gray-500">{t("availability.v2_buffer_hint")}</div>
        </div>
      </div>
    </div>
  );
}

function CapacityVsLoadOverview({ dayLoadInfo }: { dayLoadInfo: Map<string, DayLoadWidgetInfo> }) {
  const { t, tx } = useI18n();
  let totalLoad = 0;
  let totalCapacity = 0;
  let overCapacityDays = 0;
  let nearCapacityDays = 0;
  let closedWithLoadDays = 0;
  let daysWithLoad = 0;

  for (const info of dayLoadInfo.values()) {
    totalLoad += info.load;
    totalCapacity += info.capacity;
    if (info.load > 0) daysWithLoad += 1;
    if (info.flags.over_capacity_day) overCapacityDays += 1;
    if (info.flags.closed_day_with_load) closedWithLoadDays += 1;
    if (info.flags.near_capacity_day) nearCapacityDays += 1;
  }

  if (daysWithLoad === 0) return null;

  return (
    <div className="mb-4 rounded-xl border border-primary/15 bg-white p-4 space-y-2">
      <div className="text-sm font-semibold text-gray-900">{t("availability.load_title")}</div>
      <div className="text-sm text-gray-700">
        {tx("availability.load_desc", { load: totalLoad, capacity: totalCapacity })}
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        {overCapacityDays > 0 && (
          <Badge variant="outline" className="border-red-300 text-red-700">
            {tx("availability.over_capacity_days", { n: overCapacityDays })}
          </Badge>
        )}
        {closedWithLoadDays > 0 && (
          <Badge variant="outline" className="border-red-400 text-red-800">
            {tx("availability.closed_with_load", { n: closedWithLoadDays })}
          </Badge>
        )}
        {nearCapacityDays > 0 && (
          <Badge variant="outline" className="border-amber-300 text-amber-700">
            {tx("availability.near_limit_days", { n: nearCapacityDays })}
          </Badge>
        )}
        {overCapacityDays === 0 && closedWithLoadDays === 0 && nearCapacityDays === 0 && (
          <Badge variant="outline" className="border-emerald-300 text-emerald-700">
            {t("availability.load_ok")}
          </Badge>
        )}
      </div>
      <div className="text-xs text-gray-600">
        {t("availability.load_footer")}
      </div>
    </div>
  );
}

function DayLoadWidget({ info, compact = false }: { info: DayLoadWidgetInfo | undefined; compact?: boolean }) {
  const { t, tx } = useI18n();
  if (!info) return null;
  const { load, capacity, blocked, flags } = info;

  if (load === 0 && capacity === 0 && !blocked) {
    if (compact) {
      return (
        <div className="text-[11px] text-gray-500 border border-gray-100 bg-gray-50 rounded-md px-2 py-1.5 min-h-[4rem] flex flex-col justify-center gap-1">
          <div className="leading-snug">{t("availability.no_jobs_yet")}</div>
          <div className="h-[2.25rem]" aria-hidden />
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
        {t("availability.day_load_empty")}
      </div>
    );
  }

  let toneClasses = "border-gray-200 bg-gray-50 text-gray-700";
  let label = t("availability.load_ok_label");
  if (flags.over_capacity_day) {
    toneClasses = "border-red-300 bg-red-50 text-red-800";
    label = t("vendor.tracker.legend_over");
  } else if (flags.closed_day_with_load) {
    toneClasses = "border-red-300 bg-red-50 text-red-800";
    label = t("availability.load_closed_day");
  } else if (flags.near_capacity_day) {
    toneClasses = "border-amber-300 bg-amber-50 text-amber-800";
    label = t("availability.load_near_limit");
  }

  if (compact) {
    // Fixed two-row layout so day columns in the 14-day grid stay aligned even when
    // the badge copy is long (e.g. "Lavori in giornata chiusa").
    const showBadge = flags.over_capacity_day || flags.near_capacity_day || flags.closed_day_with_load;
    return (
      <div
        className={`text-[11px] border rounded-md px-2 py-1.5 flex flex-col gap-1 min-h-[4rem] justify-center ${toneClasses}`}
      >
        <div className="leading-snug">
          {tx("availability.load_compact", {
            load,
            status: blocked ? t("availability.load_compact_closed") : tx("availability.load_compact_cap", { capacity }),
          })}
        </div>
        <div className="min-h-[2.25rem] flex items-center justify-end">
          {showBadge ? (
            <Badge
              variant="outline"
              title={label}
              className="text-[10px] px-1.5 py-0.5 border-current max-w-full text-right leading-tight inline-flex items-center justify-end"
            >
              <span className="line-clamp-2 break-words text-right">{label}</span>
            </Badge>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border px-3 py-2 text-xs flex items-center justify-between gap-2 ${toneClasses}`}>
      <div>
        <span className="font-medium">{tx("availability.load_current", { load })}</span>
        {" · "}
        {blocked ? t("availability.slot_declared_closed") : tx("availability.published_capacity", { capacity })}
      </div>
      {(flags.over_capacity_day || flags.near_capacity_day || flags.closed_day_with_load) && (
        <Badge variant="outline" className="text-[10px] border-current">
          {label}
        </Badge>
      )}
    </div>
  );
}

